import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { hooks } from '../hooks';
import axiosInstance from '../services/axiosInstance';

// Preferensi tampilan per user (users.ui_preferences di backend) — pola sama dgn LanguageContext:
// nilai di localStorage dipakai dulu supaya tidak berkedip saat app dibuka, lalu disinkronkan
// dari server saat login; perubahan disimpan ke localStorage + server.
export type TaskDetailLayout = 'scroll' | 'tabs';

interface UiPreferencesContextType {
  taskDetailLayout: TaskDetailLayout;
  setTaskDetailLayout: (layout: TaskDetailLayout) => void;
}

const STORAGE_KEY = 'uiPref_taskDetailLayout';

const readStoredLayout = (): TaskDetailLayout => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'tabs' ? 'tabs' : 'scroll';
  } catch {
    return 'scroll';
  }
};

const UiPreferencesContext = createContext<UiPreferencesContextType | undefined>(undefined);

export const UiPreferencesProvider = ({ children }: { children: ReactNode }) => {
  const authSlice = hooks.useAppSelector((state) => state.authSlice);
  const [taskDetailLayout, setLayoutState] = useState<TaskDetailLayout>(readStoredLayout);

  useEffect(() => {
    if (!authSlice.isAuthenticated || !authSlice.userId) return;
    axiosInstance.get('/users/me/preferences')
      .then((res) => {
        const layout: TaskDetailLayout = res.data?.task_detail_layout === 'tabs' ? 'tabs' : 'scroll';
        setLayoutState(layout);
        try { localStorage.setItem(STORAGE_KEY, layout); } catch { /* noop */ }
      })
      .catch(() => { /* tetap pakai nilai lokal */ });
  }, [authSlice.isAuthenticated, authSlice.userId]);

  const setTaskDetailLayout = (layout: TaskDetailLayout) => {
    setLayoutState(layout);
    try { localStorage.setItem(STORAGE_KEY, layout); } catch { /* noop */ }
    if (authSlice.isAuthenticated) {
      axiosInstance.put('/users/me/preferences', { task_detail_layout: layout }).catch(() => { /* noop */ });
    }
  };

  return (
    <UiPreferencesContext.Provider value={{ taskDetailLayout, setTaskDetailLayout }}>
      {children}
    </UiPreferencesContext.Provider>
  );
};

export const useUiPreferences = () => {
  const context = useContext(UiPreferencesContext);
  if (!context) throw new Error('useUiPreferences must be used within a UiPreferencesProvider');
  return context;
};
